import { useCallback, useEffect, useRef, useState } from 'react';
import { micRelayWsUrl } from '../../pages/MicPage/micJoin';
import {
  BINARY_OP,
  LEVEL_BIN_COUNT,
  decodeAudioFinalBinary,
  type AudioFinalMeta,
} from '../../pages/MicPage/micProtocol';

export type RemoteMicStatus = 'idle' | 'connecting' | 'waiting_phone' | 'ready' | 'error';
export type RemoteMicCtrl = 'start' | 'pause' | 'resume' | 'stop' | 'cancel';

export interface RemoteMicState {
  recording: boolean;
  paused: boolean;
  durationMs: number;
  vadRunning: boolean;
}

export interface RemoteMicSession {
  status: RemoteMicStatus;
  errorCode: string | null;
  phonePresent: boolean;
  /** El móvil ya concedió el permiso de micrófono y puede grabar. */
  phoneMicReady: boolean;
  remote: RemoteMicState;
  bins: Uint8Array;
  connect: (pairingId: string, token: string) => void;
  disconnect: () => void;
  sendCtrl: (action: RemoteMicCtrl) => void;
  /** Avisa al móvil de que el carril de captura local está ocupado. */
  sendBusy: (busy: boolean) => void;
}

interface UseRemoteMicSessionOptions {
  /** Audio final ya pasado por Silero en el móvil. `vad: 'no_voice'` llega con blob vacío. */
  onAudio?: (blob: Blob, meta: AudioFinalMeta) => void;
}

const IDLE_STATE: RemoteMicState = { recording: false, paused: false, durationMs: 0, vadRunning: false };
const emptyBins = () => new Uint8Array(LEVEL_BIN_COUNT);

function concatChunks(parts: Uint8Array[], total: number): Uint8Array {
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

export function useRemoteMicSession(options: UseRemoteMicSessionOptions = {}): RemoteMicSession {
  const [status, setStatus] = useState<RemoteMicStatus>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [phonePresent, setPhonePresent] = useState(false);
  const [phoneMicReady, setPhoneMicReady] = useState(false);
  const [remote, setRemote] = useState<RemoteMicState>(IDLE_STATE);
  const [bins, setBins] = useState<Uint8Array>(emptyBins);

  const wsRef = useRef<WebSocket | null>(null);
  const closingRef = useRef(false);
  const chunksRef = useRef<Uint8Array[]>([]);
  const chunkBytesRef = useRef(0);
  const onAudioRef = useRef(options.onAudio);
  // El móvil solo manda `state` en las transiciones (start/pause/resume/stop):
  // sin interpolar aquí, el contador del escritorio se quedaría clavado en 0.
  const durationBaseRef = useRef({ durationMs: 0, at: 0 });

  useEffect(() => {
    onAudioRef.current = options.onAudio;
  }, [options.onAudio]);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    chunksRef.current = [];
    chunkBytesRef.current = 0;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      ws.close();
    }
  }, []);

  const disconnect = useCallback(() => {
    closingRef.current = true;
    closeSocket();
    setStatus('idle');
    setErrorCode(null);
    setPhonePresent(false);
    setPhoneMicReady(false);
    setRemote(IDLE_STATE);
    setBins(emptyBins());
  }, [closeSocket]);

  const handleBinary = useCallback((buffer: ArrayBuffer) => {
    const data = new Uint8Array(buffer);
    if (data.length < 2) return;

    if (data[0] === BINARY_OP.level) {
      const incoming = data.subarray(1);
      if (incoming.length !== LEVEL_BIN_COUNT) return;
      setBins(new Uint8Array(incoming));
      return;
    }

    if (data[0] === BINARY_OP.chunk) {
      const chunk = data.slice(1);
      chunksRef.current.push(chunk);
      chunkBytesRef.current += chunk.length;
      return;
    }

    if (data[0] === BINARY_OP.audioFinal) {
      const decoded = decodeAudioFinalBinary(data);
      const pending = chunksRef.current;
      chunksRef.current = [];
      const pendingBytes = chunkBytesRef.current;
      chunkBytesRef.current = 0;
      setBins(emptyBins());
      if (!decoded) return;
      const tail = decoded.audio;
      const bytes = pending.length
        ? concatChunks([...pending, tail], pendingBytes + tail.length)
        : tail;
      const blob = new Blob([bytes as BlobPart], { type: decoded.meta.mime || 'audio/webm' });
      onAudioRef.current?.(blob, decoded.meta);
    }
  }, []);

  const connect = useCallback((pairingId: string, token: string) => {
    closingRef.current = false;
    closeSocket();
    setErrorCode(null);
    setPhonePresent(false);
    setPhoneMicReady(false);
    setRemote(IDLE_STATE);
    setBins(emptyBins());
    setStatus('connecting');

    const socket = new WebSocket(micRelayWsUrl());
    socket.binaryType = 'arraybuffer';
    wsRef.current = socket;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'hello', role: 'desktop', pairingId, token }));
      setStatus('waiting_phone');
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') {
        if (event.data instanceof ArrayBuffer) handleBinary(event.data);
        return;
      }
      let msg: {
        type?: string;
        code?: string;
        present?: boolean;
        role?: string;
        recording?: boolean;
        paused?: boolean;
        durationMs?: number;
        vadRunning?: boolean;
        micReady?: boolean;
      };
      try {
        msg = JSON.parse(event.data) as typeof msg;
      } catch {
        return;
      }

      if (msg.type === 'error') {
        setErrorCode(msg.code || 'relay_error');
        setStatus('error');
        return;
      }
      if (msg.type === 'peer' && msg.role === 'phone') {
        const present = Boolean(msg.present);
        setPhonePresent(present);
        // El móvil confirma el permiso con su primer 'state'.
        if (!present) setPhoneMicReady(false);
        setStatus(present ? 'ready' : 'waiting_phone');
        return;
      }
      if (msg.type === 'peer_gone' && msg.role === 'phone') {
        setPhonePresent(false);
        setPhoneMicReady(false);
        setStatus('waiting_phone');
        setRemote(IDLE_STATE);
        setBins(emptyBins());
        return;
      }
      if (msg.type === 'state') {
        // Una grabación nueva invalida cualquier resto del buffer anterior
        // (frames sueltos tras un corte): concatenarlos produciría un WebM con
        // dos cabeceras EBML que Vertex rechaza con 400.
        if (msg.recording && chunksRef.current.length) {
          chunksRef.current = [];
          chunkBytesRef.current = 0;
        }
        setPhoneMicReady(msg.micReady === true);
        const durationMs = typeof msg.durationMs === 'number' ? msg.durationMs : 0;
        durationBaseRef.current = { durationMs, at: Date.now() };
        setRemote({
          recording: Boolean(msg.recording),
          paused: Boolean(msg.paused),
          durationMs,
          vadRunning: Boolean(msg.vadRunning),
        });
      }
    };

    socket.onerror = () => {
      if (closingRef.current) return;
      setErrorCode('relay_error');
      setStatus('error');
    };

    socket.onclose = () => {
      if (closingRef.current) return;
      setPhonePresent(false);
      setPhoneMicReady(false);
      // Sin este reset, un corte mientras el móvil procesaba (vadRunning) dejaría
      // el escritorio bloqueado esperando un audio que ya no va a llegar.
      setRemote(IDLE_STATE);
      setBins(emptyBins());
      setStatus((prev) => (prev === 'error' ? prev : 'idle'));
    };
  }, [closeSocket, handleBinary]);

  const sendCtrl = useCallback((action: RemoteMicCtrl) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ctrl', action }));
    }
  }, []);

  const sendBusy = useCallback((busy: boolean) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'busy', busy }));
    }
  }, []);

  useEffect(() => () => {
    closingRef.current = true;
    closeSocket();
  }, [closeSocket]);

  const ticking = remote.recording && !remote.paused;
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => {
      const base = durationBaseRef.current;
      setRemote((prev) =>
        prev.recording && !prev.paused
          ? { ...prev, durationMs: base.durationMs + (Date.now() - base.at) }
          : prev,
      );
    }, 250);
    return () => window.clearInterval(id);
  }, [ticking]);

  return { status, errorCode, phonePresent, phoneMicReady, remote, bins, connect, disconnect, sendCtrl, sendBusy };
}
